/**
 * PosDiagnosticsPanel — live readout of the POS background services.
 *
 * Renders on the Printer tab so cashiers/managers can see, without a
 * dev console, whether the POS is actually polling, printing, and
 * chiming. All values are subscribed via posDiagnostics.subscribe so
 * the panel updates immediately on every event without polling.
 *
 * ZERO customer PII is displayed — only IDs, HTTP status codes, and
 * timestamps.
 */
import React, { useSyncExternalStore } from 'react';
import { getSnapshot, subscribe } from '../../services/pos/posDiagnostics';

const fmtTs = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const now = Date.now();
    const diff = Math.round((now - d.getTime()) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.round(diff / 60)} min ago`;
    return d.toLocaleTimeString('en-GB');
  } catch (_e) { return iso; }
};

const StatusPill = ({ ok, label, warn = false, error = false }) => {
  let cls = 'bg-neutral-200 text-neutral-700';
  if (error) cls = 'bg-red-100 text-red-800';
  else if (warn) cls = 'bg-amber-100 text-amber-800';
  else if (ok) cls = 'bg-emerald-100 text-emerald-800';
  return <span className={`px-2 py-0.5 rounded text-xs font-semibold ${cls}`}>{label}</span>;
};

const Row = ({ k, v, testid }) => (
  <div className="flex justify-between text-xs py-1 border-b border-neutral-100 last:border-b-0">
    <span className="text-neutral-500">{k}</span>
    <span className="text-neutral-800 font-mono text-right" data-testid={testid}>
      {v ?? '—'}
    </span>
  </div>
);

export default function PosDiagnosticsPanel() {
  // useSyncExternalStore gives us a re-render on every notify() call
  // from posDiagnostics — no polling, no interval, no stale reads.
  const snap = useSyncExternalStore(subscribe, getSnapshot);

  const bgOk = snap.services.posBgMounted;
  const apOk = snap.services.autoPrintRunning;
  const noOk = snap.services.newOrderPollerRunning;
  const brOk = snap.services.androidBridgeAvailable;
  const sound = snap.services.soundState;

  const soundError = sound === 'ERROR';
  const soundWarn = sound === 'BLOCKED';

  return (
    <details
      className="rounded-xl border border-neutral-200 bg-white p-3 mt-4 group"
      data-testid="pos-diagnostics-panel"
    >
      <summary className="cursor-pointer flex items-center justify-between text-sm font-semibold list-none">
        <span>Diagnostics</span>
        <span className="text-xs text-neutral-500 flex items-center gap-2">
          <StatusPill ok={bgOk && apOk && noOk} error={!bgOk} warn={bgOk && !(apOk && noOk)} label={bgOk && apOk && noOk ? 'HEALTHY' : 'CHECK'} />
        </span>
      </summary>

      <div className="mt-3">
        <div className="text-xs font-semibold text-neutral-600 mb-1">Services</div>
        <div className="grid grid-cols-2 gap-1">
          <Row k="Background Services" v={<StatusPill ok={bgOk} error={!bgOk} label={bgOk ? 'RUNNING' : 'STOPPED'} />} testid="diag-bg" />
          <Row k="Auto Print Controller" v={<StatusPill ok={apOk} error={!apOk} label={apOk ? 'RUNNING' : 'STOPPED'} />} testid="diag-ap" />
          <Row k="Order Poller" v={<StatusPill ok={noOk} error={!noOk} label={noOk ? 'RUNNING' : 'STOPPED'} />} testid="diag-no" />
          <Row k="Android Bridge" v={<StatusPill ok={brOk} error={!brOk} label={brOk ? 'AVAILABLE' : 'UNAVAILABLE'} />} testid="diag-br" />
          <Row k="Sound" v={<StatusPill ok={sound === 'READY'} warn={soundWarn} error={soundError} label={sound} />} testid="diag-snd" />
        </div>

        <div className="text-xs font-semibold text-neutral-600 mt-3 mb-1">New-Order Poller</div>
        <Row k="Last poll" v={fmtTs(snap.newOrderPoll.lastPollAt)} testid="diag-no-lastpoll" />
        <Row k="Last HTTP" v={snap.newOrderPoll.lastHttpStatus} testid="diag-no-http" />
        <Row k="Last count" v={snap.newOrderPoll.lastCount} testid="diag-no-count" />
        <Row k="Last error" v={snap.newOrderPoll.lastError} />
        <Row k="Last chime" v={fmtTs(snap.newOrderPoll.lastChimeAt)} testid="diag-no-chime" />

        <div className="text-xs font-semibold text-neutral-600 mt-3 mb-1">Auto-Print</div>
        <Row k="Last queue poll" v={fmtTs(snap.autoPrint.lastPollAt)} testid="diag-ap-lastpoll" />
        <Row k="Last HTTP" v={snap.autoPrint.lastHttpStatus} testid="diag-ap-http" />
        <Row k="Printable found" v={snap.autoPrint.lastPrintableCount} testid="diag-ap-count" />
        <Row k="Last order detected" v={snap.autoPrint.lastOrderDetected ? (snap.autoPrint.lastOrderDetected.slice(-8)) : '—'} />
        <Row k="Last print attempt" v={fmtTs(snap.autoPrint.lastPrintAttemptAt)} />
        <Row k="Print attempt order" v={snap.autoPrint.lastPrintAttemptOrder ? snap.autoPrint.lastPrintAttemptOrder.slice(-8) : '—'} />
        <Row
          k="Last result"
          v={
            <StatusPill
              ok={snap.autoPrint.lastPrintResult === 'SUCCESS'}
              error={snap.autoPrint.lastPrintResult === 'FAILED'}
              label={snap.autoPrint.lastPrintResult}
            />
          }
          testid="diag-ap-result"
        />
        <Row k="Last error" v={snap.autoPrint.lastPrintError} />

        <div className="text-xs font-semibold text-neutral-600 mt-3 mb-1">Recovery</div>
        <Row k="Last app resume" v={fmtTs(snap.resume.lastResumeAt)} />
        <Row k="Resume reason" v={snap.resume.lastResumeReason} />
      </div>
    </details>
  );
}
